/* Roboflow service module */

import axios from 'axios';

/**
 * Validate the image file before sending to Roboflow
 * @param {File} file - The image file to validate
 * @throws {Error} If file is invalid
 */
function validateImageFile(file) {
  if (!file) {
    throw new Error('No file provided');
  }

  if (!(file instanceof File)) {
    throw new Error('Invalid file object');
  }

  // Check if it's an image file
  if (!file.type.startsWith('image/')) {
    throw new Error('File must be an image');
  }

  // Check file size (10MB limit)
  const MAX_SIZE = 5 * 1024 * 1024; // 5MB
  if (file.size > MAX_SIZE) {
    throw new Error('Image file size must be less than 5MB');
  }
}

/**
 * Calls the Roboflow API to detect features in an image.
 * @param {File} file - The image file to analyze
 * @param {string} apiKey - Your Roboflow API key
 * @param {string} datasetSlug - The url-safe version of the dataset name
 * @param {number} versionNumber - The version number of your dataset
 * @returns {Promise<object>} - Prediction data from Roboflow API
 * @throws {Error} If validation fails or API call fails
 */
export async function getAutoDetectedFeatures(file, apiKey, datasetSlug, versionNumber) {
  try {
    // Validate parameters
    if (!apiKey) {
      throw new Error('API key is required');
    }
    if (!datasetSlug) {
      throw new Error('Dataset slug is required');
    }
    if (!versionNumber) {
      throw new Error('Version number is required');
    }

    // Validate file
    validateImageFile(file);

    const formData = new FormData();
    formData.append('file', file);

    const response = await axios({
      method: 'POST',
      url: `https://detect.roboflow.com/${datasetSlug}/${versionNumber}`,
      params: {
        api_key: apiKey
      },
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 30000 // 30 second timeout
    });

    if (!response.data) {
      throw new Error('No data received from Roboflow API');
    }

    if (!response.data.predictions) {
      return { predictions: [] }; // Return empty predictions array if none found
    }

    // Validate, clean, and normalize predictions (0-1)
    const imageWidth = response.data.image?.width || 0;
    const imageHeight = response.data.image?.height || 0;

    const cleanPredictions = response.data.predictions.map(pred => {
      // First validate the values
      const x = typeof pred.x === 'number' ? pred.x : 0;
      const y = typeof pred.y === 'number' ? pred.y : 0;
      const width = typeof pred.width === 'number' ? pred.width : 0;
      const height = typeof pred.height === 'number' ? pred.height : 0;

      // Normalize coordinates keeping center-point format (Roboflow already returns center-point)
      return {
        class: pred.class || '',
        confidence: typeof pred.confidence === 'number' ? pred.confidence : 0,
        x: imageWidth ? x / imageWidth : 0,
        y: imageHeight ? y / imageHeight : 0,
        width: imageWidth ? width / imageWidth : 0,
        height: imageHeight ? height / imageHeight : 0
      };
    });

    return {
      predictions: cleanPredictions,
      time: response.data.time || 0,
      image: {
        width: response.data.image?.width || 0,
        height: response.data.image?.height || 0
      }
    };

  } catch (error) {
    console.error('Error calling Roboflow API:', error);
    
    // Handle specific error cases
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Request timed out. Please try again.');
      }
      if (error.response) {
        switch (error.response.status) {
          case 401:
            throw new Error('Invalid API key');
          case 404:
            throw new Error('Dataset or version not found');
          case 413:
            throw new Error('Image file is too large');
          case 429:
            throw new Error('Too many requests. Please try again later.');
          default:
            throw new Error(`API error: ${error.response.data?.error || error.message}`);
        }
      }
    }
    
    throw new Error('Failed to process image. Please try again.');
  }
}
